/**
 * Manages the free bracelet product state across collection and product pages.
 *
 * This script monitors cart changes and updates the UI to enable/disable purchase buttons
 * based on whether a free bracelet is already in the cart. It handles two scenarios:
 *
 * 1. Product Detail Page (PDP): Shows the "Unavailable" button and hides the submit button
 *    when a free bracelet is already in the cart, preventing duplicate free bracelet purchases.
 *
 * 2. Collection Page: Applies the same logic across multiple product cards on the
 *    free bracelets collection page.
 *
 * The script uses a MutationObserver to monitor cart drawer changes in the upCart
 * shadow DOM element and updates the UI accordingly through cart state callbacks.
 *
 * @function FreeBracelet
 * @returns {void}
 */
function FreeBracelet() {
  const isFreeBraceletCollectionPage = document.querySelector('.free-bracelets-collection-page');
  const isFreeBraceletProductPage = document.querySelector('.free-bracelet-product-page');

  // Product defail api
  function productDetails(handle) {
    return window
      .fetch(`/products/${handle}.js`, {
        credentials: 'same-origin',
        method: 'GET',
      })
      .then((response) => {
        return response.json();
      });
  }

  function monitorCartChange({ handleCartCallback, handleUIUpdate }) {
    // use an observer to monitor changes in the cart drawer
    const observer = new MutationObserver(() => {
      handleCartCallback()
        .then((currentCartState) => {
          handleUIUpdate(currentCartState);
        })
        .catch((error) => {
          console.error('Error fetching cart data:', error);
        });
    });

    const upCart = document.getElementById('upCart');
    // cartDrawer is inside upCart, so we need to get the cart drawer element from upCart
    // Element is in shadow root, so we need to use shadowRoot to get the cart drawer element
    if (!upCart) {
      console.error('upCart element not found. Unable to monitor cart changes.');
      return;
    }
    const cartDrawer = upCart ? upCart.shadowRoot.querySelector('.upcart-cart') : null;
    if (cartDrawer) {
      observer.observe(cartDrawer, { childList: true, subtree: true });
    } else {
      console.error('Cart drawer element not found. Unable to monitor cart changes.');
    }
  }

  function waitForUpCartShadowRoot({ interval = 200, timeout = 10000 } = {}) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const poller = window.setInterval(() => {
        const upCart = document.getElementById('upCart');

        if (upCart && upCart.shadowRoot) {
          window.clearInterval(poller);
          resolve(upCart.shadowRoot);
          return;
        }

        if (Date.now() - startTime >= timeout) {
          window.clearInterval(poller);
          reject(new Error('Timed out waiting for upCart shadow root.'));
        }
      }, interval);
    });
  }

  // Cart api
  function getCart(handleCallback) {
    return window
      .fetch(`/cart.js`)
      .then((response) => response.json())
      .then((data) => {
        handleCallback(data);
      });
  }

  // This function will be handling the pdp state by showing and hiding the submit and unavailable button
  async function init() {
    const currentBraceletContainer = document.querySelector('product-info');
    const submitButton = currentBraceletContainer.querySelector('.product-form__submit');
    const currentBraceletHandle = currentBraceletContainer.getAttribute('data-handle');
    const notAvailableButton = currentBraceletContainer.querySelector('.product-form__unavailable-button');
    // current bracelet details.
    const currentBraceletDetail = await productDetails(currentBraceletHandle);

    // This function will be handling the pdp state by showing and hiding the submit and unavailable button
    function handleBraceletPdpState(cart) {
      // reverting the disabled state of submit button
      if (submitButton.children[0].innerText !== 'Out of stock') {
        submitButton.removeAttribute('disabled');
      }

      if (cart && currentBraceletDetail) {
        const isFreeBraceletInCart = cart.items.filter((item) => {
          return item.product_title.includes('FREE') || item.product_id === Number(cart.attributes.is_free_bracelet_id);
        });

        // If free bracelet is there in cart and the current pdp is of free bracelet
        if (
          isFreeBraceletInCart &&
          isFreeBraceletInCart.length &&
          currentBraceletDetail.tags.includes('freebracelet')
        ) {
          submitButton.classList.add('hidden');
          notAvailableButton.classList.remove('hidden');
        } else {
          submitButton.classList.remove('hidden');
          notAvailableButton.classList.add('hidden');
        }
      }
    }

    // Cart api
    function getPDPCart() {
      // Making submit button disabled for initial load
      submitButton.setAttribute('disabled', true);

      return getCart(handleBraceletPdpState);
    }

    waitForUpCartShadowRoot()
      .then(() => {
        monitorCartChange({
          handleCartCallback: getPDPCart,
          handleUIUpdate: handleBraceletPdpState,
        });
        getPDPCart();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }

  function initFreeBraceletsPage() {
    // This function will be handling the pdp state by showing and hiding the submit and unavailable button
    function handleFreeBraceletPage(cart) {
      const submitButtons = document.querySelectorAll('.product-form__cart-submit');
      const notAvailableButtons = document.querySelectorAll('.product-form__unavailable-button');
      submitButtons.forEach((button) => {
        // reverting the disabled state of submit button
        if (button.children[0].innerText !== 'Out of stock') {
          button.removeAttribute('disabled');
        }
      });

      if (cart) {
        const isFreeBraceletInCart = cart.items.filter((item) => {
          return item.product_title.includes('FREE') || item.product_id === Number(cart.attributes.is_free_bracelet_id);
        });

        // If free bracelet is there in cart and the current pdp is of free bracelet
        if (isFreeBraceletInCart && isFreeBraceletInCart.length) {
          submitButtons.forEach((button) => {
            button.classList.add('hidden');
          });
          notAvailableButtons.forEach((button) => {
            button.classList.remove('hidden');
          });
        } else {
          submitButtons.forEach((button) => {
            button.classList.remove('hidden');
          });
          notAvailableButtons.forEach((button) => {
            button.classList.add('hidden');
          });
        }
      }
    }

    // Cart api
    function getCollectionPageCart() {
      // Making all the submit buttons disabled for initial load
      const submitButtons = document.querySelectorAll('.product-form__submit');
      submitButtons.forEach((button) => {
        button.setAttribute('disabled', true);
      });

      return getCart(handleFreeBraceletPage);
    }

    waitForUpCartShadowRoot()
      .then(() => {
        monitorCartChange({
          handleCartCallback: getCollectionPageCart,
          handleUIUpdate: handleFreeBraceletPage,
        });
        getCollectionPageCart();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }

  if (isFreeBraceletProductPage) {
    init();
  }

  if (isFreeBraceletCollectionPage) {
    initFreeBraceletsPage();
  }
}

const isFreeBraceletCollectionPage = document.querySelector('.free-bracelets-collection-page');
if (document.body.classList.contains('template-product-bracelets') || isFreeBraceletCollectionPage) {
  FreeBracelet();
}
